import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Package,
  MapPin,
  BarChart3,
  LogOut,
  Menu,
  X,
  User,
  Users,
  Plus,
  Upload,
  Search,
  FileText,
  UserCircle,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  CreditCard,
  RotateCcw,
  Receipt,
  Printer,
  PanelLeftClose,
  PanelLeftOpen,
  Languages
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const SIDEBAR_COLLAPSE_KEY = 'AINSOFT_SIDEBAR_COLLAPSED';

const Sidebar = ({ user, onLogout }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [expandedMenus, setExpandedMenus] = useState({});
  const location = useLocation();
  const { t, language, setLanguage, isRTL } = useLanguage();

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSE_KEY, String(isCollapsed));
    } catch {
      // ignore
    }
  }, [isCollapsed]);

  // Close mobile drawer on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const menuItems = [
    { path: '/', icon: Home, labelKey: 'sidebar.dashboard' },
    { path: '/new-order', icon: Plus, labelKey: 'sidebar.newOrder' },
    { path: '/upload-orders', icon: Upload, labelKey: 'sidebar.uploadOrders' },
    { path: '/deliveries', icon: Package, labelKey: 'sidebar.deliveries' },
    {
      path: '/tracking',
      icon: MapPin,
      labelKey: 'sidebar.tracking',
      hasSubmenu: true,
      submenu: [
        { path: '/tracking', icon: Search, labelKey: 'sidebar.trackOrders' },
        { path: '/track-by-customer', icon: User, labelKey: 'sidebar.trackByCustomer' },
        { path: '/track-by-bill', icon: FileText, labelKey: 'sidebar.trackByBillNo' }
      ]
    },
    { path: '/my-customers', icon: Users, labelKey: 'sidebar.myCustomers' },
    { path: '/batch-printing', icon: Printer, labelKey: 'sidebar.batchPrinting' },
    {
      path: '/reports',
      icon: BarChart3,
      labelKey: 'sidebar.reports',
      hasSubmenu: true,
      submenu: [
        { path: '/order-reports', icon: FileText, labelKey: 'sidebar.orderReports' },
        { path: '/customer-report', icon: User, labelKey: 'sidebar.customerReport' },
        { path: '/payment-report', icon: CreditCard, labelKey: 'sidebar.paymentReport' },
        { path: '/return-report', icon: RotateCcw, labelKey: 'sidebar.returnReport' },
        { path: '/vat-report', icon: Receipt, labelKey: 'sidebar.vatReport' }
      ]
    },
    { path: '/settings', icon: UserCircle, labelKey: 'sidebar.profile' }
  ];

  const isActive = (path) => location.pathname === path;
  const isSubmenuActive = (submenu) => submenu.some((item) => isActive(item.path));

  const toggleSubmenu = (path) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const ExpandIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar — right side when Arabic */}
      <div
        dir={isRTL ? 'rtl' : 'ltr'}
        className={`
          fixed lg:static inset-y-0 z-50 bg-gradient-to-b from-white to-primary-50
          transform transition-all duration-300 ease-in-out flex-shrink-0
          ${isRTL
            ? 'right-0 left-auto border-l border-primary-200'
            : 'left-0 right-auto border-r border-primary-200'
          }
          ${isCollapsed ? 'lg:w-20' : 'lg:w-64'}
          w-64
          ${isMobileOpen
            ? 'translate-x-0'
            : isRTL
              ? 'translate-x-full lg:translate-x-0'
              : '-translate-x-full lg:translate-x-0'
          }
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="gradient-header p-4 lg:p-5">
            <div className="flex items-center justify-between gap-2">
              <div className={`flex items-center gap-3 min-w-0 ${isCollapsed ? 'lg:justify-center lg:w-full' : ''}`}>
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center p-1 flex-shrink-0">
                  <img
                    src="/images/logo.png"
                    alt={t('sidebar.brandTitle')}
                    className="w-full h-full object-contain brightness-0 invert"
                  />
                </div>
                {!isCollapsed && (
                  <div className="min-w-0 hidden lg:block">
                    <h1 className="text-white font-bold text-lg truncate">{t('sidebar.brandTitle')}</h1>
                    <p className="text-primary-100 text-sm truncate">{t('sidebar.brandSubtitle')}</p>
                  </div>
                )}
                <div className="min-w-0 lg:hidden">
                  <h1 className="text-white font-bold text-lg truncate">{t('sidebar.brandTitle')}</h1>
                  <p className="text-primary-100 text-sm truncate">{t('sidebar.brandSubtitle')}</p>
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Desktop collapse toggle */}
                <button
                  type="button"
                  onClick={() => setIsCollapsed((prev) => !prev)}
                  className="hidden lg:flex text-white hover:text-primary-100 p-1.5 rounded-md hover:bg-white/10"
                  title={isCollapsed ? t('common.expand') : t('common.collapse')}
                  aria-label={isCollapsed ? t('common.expand') : t('common.collapse')}
                >
                  {isCollapsed
                    ? (isRTL ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeftOpen className="w-5 h-5" />)
                    : (isRTL ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />)}
                </button>

                {/* Mobile close */}
                <button
                  type="button"
                  onClick={() => setIsMobileOpen(false)}
                  className="lg:hidden text-white hover:text-primary-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* User Profile + Language */}
          <div className={`border-b border-primary-200 ${isCollapsed ? 'p-3' : 'p-4 lg:p-5'}`}>
            <div className={`flex items-center gap-3 ${isCollapsed ? 'lg:justify-center' : ''}`}>
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-white" />
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.Name || t('sidebar.userFallback')}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user?.EmailId || ''}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.MobileNo1 || ''}</p>
                </div>
              )}
            </div>

            {/* Language switch — after profile data */}
            {!isCollapsed ? (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1.5">
                  <Languages className="w-3.5 h-3.5" />
                  {t('common.language')}
                </p>
                <div className="flex rounded-lg border border-primary-200 overflow-hidden bg-white">
                  <button
                    type="button"
                    onClick={() => setLanguage('en')}
                    className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors ${
                      language === 'en'
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-600 hover:bg-primary-50'
                    }`}
                  >
                    {t('common.english')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLanguage('ar')}
                    className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors ${
                      language === 'ar'
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-600 hover:bg-primary-50'
                    }`}
                  >
                    {t('common.arabic')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')}
                className="hidden lg:flex mt-3 w-full items-center justify-center rounded-lg border border-primary-200 bg-white px-2 py-2 text-xs font-bold text-primary-700 hover:bg-primary-50"
                title={t('common.language')}
              >
                {language === 'en' ? 'ع' : 'EN'}
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className={`flex-1 py-4 space-y-1 overflow-y-auto ${isCollapsed ? 'px-2' : 'px-3'}`}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isExpanded = expandedMenus[item.path];
              const hasActiveSubmenu = item.hasSubmenu && isSubmenuActive(item.submenu);
              const label = t(item.labelKey);

              if (item.hasSubmenu) {
                return (
                  <div key={item.path}>
                    <button
                      type="button"
                      onClick={() => {
                        if (isCollapsed) {
                          setIsCollapsed(false);
                          setExpandedMenus((prev) => ({ ...prev, [item.path]: true }));
                          return;
                        }
                        toggleSubmenu(item.path);
                      }}
                      title={isCollapsed ? label : undefined}
                      className={`
                        w-full flex items-center rounded-lg text-sm font-medium transition-all duration-200
                        ${isCollapsed ? 'justify-center px-2 py-3' : 'justify-between px-4 py-3'}
                        ${hasActiveSubmenu
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                          : 'text-gray-700 hover:bg-gradient-to-r hover:from-primary-100 hover:to-primary-200 hover:text-primary-700'
                        }
                      `}
                    >
                      <div className={`flex items-center ${isCollapsed ? '' : 'gap-3'}`}>
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        {!isCollapsed && <span>{label}</span>}
                      </div>
                      {!isCollapsed && (
                        isExpanded ? <ChevronDown className="w-4 h-4" /> : <ExpandIcon className="w-4 h-4" />
                      )}
                    </button>

                    {!isCollapsed && isExpanded && (
                      <div className={`${isRTL ? 'mr-5' : 'ml-5'} mt-1 space-y-1`}>
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const subLabel = t(subItem.labelKey);
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className={`
                                flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                                ${isActive(subItem.path)
                                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                                  : 'text-gray-600 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 hover:text-primary-700'
                                }
                              `}
                            >
                              <SubIcon className="w-4 h-4" />
                              <span>{subLabel}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={isCollapsed ? label : undefined}
                  className={`
                    flex items-center rounded-lg text-sm font-medium transition-all duration-200
                    ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3'}
                    ${isActive(item.path)
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                      : 'text-gray-700 hover:bg-gradient-to-r hover:from-primary-100 hover:to-primary-200 hover:text-primary-700'
                    }
                  `}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!isCollapsed && <span>{label}</span>}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className={`border-t border-primary-200 ${isCollapsed ? 'p-2' : 'p-4'}`}>
            <button
              type="button"
              onClick={onLogout}
              title={isCollapsed ? t('sidebar.logout') : undefined}
              className={`
                w-full flex items-center rounded-lg text-sm font-medium text-gray-700
                hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 hover:text-red-700 transition-all duration-200
                ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3'}
              `}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && <span>{t('sidebar.logout')}</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu button */}
      <button
        type="button"
        onClick={() => setIsMobileOpen(true)}
        className={`lg:hidden fixed top-4 z-50 p-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg shadow-lg ${
          isRTL ? 'right-4' : 'left-4'
        }`}
      >
        <Menu className="w-5 h-5" />
      </button>
    </>
  );
};

export default Sidebar;
